"use client";

import { Check } from "lucide-react";
import type { ReactNode } from "react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/ui/shadcn-components/dropdown-menu";
import { COMPOSER_MODES, type ComposerMode, type ComposerModeId } from "./modes";
import { cn } from "@/shared/lib/utils/cn";

interface ModeMenuProps {
  selectedId: ComposerModeId;
  onSelect: (mode: ComposerMode) => void;
  trigger: ReactNode;
}

export function ModeMenu({ selectedId, onSelect, trigger }: ModeMenuProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent
        side="top"
        align="start"
        sideOffset={8}
        className="w-64 overflow-hidden rounded-xl p-0 shadow-[0_8px_32px_rgba(0,0,0,0.12)]"
      >
        {COMPOSER_MODES.map((mode) => {
          const selected = mode.id === selectedId;
          const Icon = mode.Icon;

          return (
            <DropdownMenuItem
              key={mode.id}
              onSelect={() => onSelect(mode)}
              className={cn(
                "items-start gap-3 rounded-none px-4 py-3 focus:bg-muted/40",
                selected && "bg-muted/30",
              )}
            >
              <Icon
                className={cn(
                  "mt-0.5 size-4 shrink-0",
                  selected ? "text-primary" : "text-muted-foreground/50",
                )}
              />
              <div className="min-w-0 flex-1">
                <p
                  className={cn(
                    "text-[13px] leading-tight font-medium",
                    selected ? "text-foreground" : "text-foreground/80",
                  )}
                >
                  {mode.title}
                </p>
                <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground/60">
                  {mode.description}
                </p>
              </div>
              {selected ? <Check className="mt-0.5 size-3.5 shrink-0 text-primary" /> : null}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
